/**
 * Iter 231 — Portal Team Manager (inline, no modals).
 *
 * Lets a portal-owner add/remove teammates on their customer record,
 * assign one of 4 presets (Owner / Admin / Accounts / Maintenance)
 * or roll a Custom permission set.
 *
 * Trust Contract:
 *  • NO Modal usage. Slide-overs and inline cards only.
 *  • All long-text uses normal inputs (no Voice* helpers needed —
 *    this is admin not capture UX).
 *  • Every interactive element carries a stable data-testid.
 */
import { useEffect, useMemo, useState } from "react";
import {
  UserPlus,
  Trash2,
  KeyRound,
  Copy,
  ShieldCheck,
  CheckCircle2,
  X,
  Settings2,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const PRESET_ORDER = ["owner", "admin", "accounts", "maintenance", "custom"];

const PERMISSION_GROUPS = [
  {
    label: "Work",
    keys: [
      "portal.jobs.read",
      "portal.tickets.read",
      "portal.tickets.write",
      "portal.photos.read",
      "portal.performance.capture",
      "portal.performance.read",
    ],
  },
  {
    label: "Finance",
    keys: [
      "portal.quotes.read",
      "portal.invoices.read",
    ],
  },
  {
    // [Iter 349.q — Dirk, 2026-02-27] Per-folder document access.
    label: "Documents (per folder)",
    keys: [
      "portal.docs.contracts.read",
      "portal.docs.proposals.read",
      "portal.docs.manuals.read",
      "portal.docs.warranties.read",
      "portal.docs.account.read",
      "portal.docs.notices.read",
      "portal.docs.photos.read",
      "portal.docs.compliance.read",
      "portal.docs.reports.read",
    ],
  },
  {
    label: "Profile & orders",
    keys: [
      "portal.profile.read",
      "portal.profile.write",
      "portal.orders.write",
      "portal.users.manage",
      "portal.announcements.read",
      "portal.documents.read",
    ],
  },
];

const PERM_LABELS = {
  "portal.jobs.read": "View jobs",
  "portal.tickets.read": "View tickets",
  "portal.tickets.write": "Log / reply to tickets",
  "portal.photos.read": "View photos & gallery",
  "portal.quotes.read": "View quotes",
  "portal.invoices.read": "View invoices & statements",
  "portal.documents.read": "View documents (uncatalogued fallback)",
  "portal.profile.read": "View company profile",
  "portal.profile.write": "Edit company profile",
  "portal.orders.write": "Place orders",
  "portal.users.manage": "Manage portal team",
  "portal.performance.capture": "Log sheet — daily readings",
  "portal.performance.read": "Performance analytics + reports",
  "portal.announcements.read": "View announcements",
  // Per-folder document perms
  "portal.docs.contracts.read":   "Contracts & SLAs",
  "portal.docs.proposals.read":   "Proposals & Designs",
  "portal.docs.manuals.read":     "Manuals & Datasheets",
  "portal.docs.warranties.read":  "Warranties",
  "portal.docs.account.read":     "Account Docs",
  "portal.docs.notices.read":     "Notices & Bulletins",
  "portal.docs.photos.read":      "Photos & Site Plans",
  "portal.docs.compliance.read":  "Compliance (SHEQ / Insurance / Permits)",
  "portal.docs.reports.read":     "Reports & Technical",
};

export default function PortalTeamManager({ embedded = false }) {
  const { user: me } = useAuth();
  const [catalogue, setCatalogue] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingUid, setEditingUid] = useState(null);
  const [confirmDeleteUid, setConfirmDeleteUid] = useState(null);
  const [resetForUid, setResetForUid] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [tempCreds, setTempCreds] = useState(null);

  // Add-form state
  const [emailInput, setEmailInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [presetInput, setPresetInput] = useState("maintenance");
  const [customInput, setCustomInput] = useState({});
  const [pwdInput, setPwdInput] = useState("");
  // Iter 427.p41 — Preflight lookup: does this email already have a
  // Hidroverse account? If yes, hide the password field entirely
  // because the identity's password can't be overwritten by a
  // second-customer invite (Slack/Google Workspace identity model).
  const [emailLookup, setEmailLookup] = useState(null);
  // Iter 400.14 → retired in Iter 427.p41. `customer_role` is now
  // DERIVED from the preset on the backend — no separate dropdown.

  const canManage = useMemo(() => {
    if (!me) return false;
    if (me.role !== "client") return true;
    // 2026-02 — Read from the canonical field name `effective_perms`
    // (previously misread as `effective_portal_perms` which doesn't
    // exist on the /auth/me payload — that mismatch defaulted to
    // "manage=true" for every client and let non-managers see the
    // invite/remove buttons).
    const eff = me.effective_perms || {};
    if (!me.portal_preset && Object.keys(eff).length === 0) return true;
    return Boolean(eff["portal.users.manage"]);
  }, [me]);

  const refresh = async () => {
    setLoading(true);
    setErr("");
    try {
      const [cat, lst] = await Promise.all([
        api.get("/portal/perm-catalogue"),
        api.get("/portal/users"),
      ]);
      setCatalogue(cat.data);
      setUsers(lst.data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load team"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const resetAddForm = () => {
    setEmailInput("");
    setNameInput("");
    setPresetInput("maintenance");
    setCustomInput({});
    setPwdInput("");
    setEmailLookup(null);
    setAdding(false);
  };

  // Iter 427.p41 — Preflight the email on blur so we can hide the
  // password field for identities that already have an account.
  const preflightEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !/@/.test(email)) {
      setEmailLookup(null);
      return;
    }
    try {
      const { data } = await api.get(
        `/portal/users/lookup?email=${encodeURIComponent(email)}`,
      );
      setEmailLookup(data);
      if (data?.exists && data?.has_password) {
        // Password owned by the identity — never overwrite. Blank the
        // field so the operator can't try.
        setPwdInput("");
      }
    } catch (_) {
      setEmailLookup(null);
    }
  };

  const submitAdd = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setErr("Email is required.");
      return;
    }
    if (emailLookup?.exists && emailLookup?.already_on_this_customer) {
      setErr("This email is already a portal user for your customer.");
      return;
    }
    if (emailLookup?.exists && emailLookup?.is_staff) {
      setErr("This email belongs to a staff account and can't be added.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        email,
        name: nameInput.trim() || null,
        // Iter 427.p41 — never send a password on cross-customer add.
        // Backend also ignores it, this is a UX belt-and-braces.
        password: emailLookup?.exists ? null : (pwdInput.trim() || null),
        portal_preset: presetInput,
      };
      if (presetInput === "custom") {
        body.portal_permissions = customInput;
      }
      const { data } = await api.post("/portal/users", body);
      if (data?.cross_customer_add) {
        setTempCreds({
          email: data.email,
          password: null,
          passwordFree: true,
          crossCustomer: true,
        });
      } else if (data?.email) {
        setTempCreds({
          email: data.email,
          password: pwdInput.trim() || null,
        });
      }
      resetAddForm();
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not create teammate"));
    } finally {
      setBusy(false);
    }
  };

  const submitChangePreset = async (uid, preset, customOverrides) => {
    setBusy(true);
    setErr("");
    try {
      const body = { portal_preset: preset };
      if (preset === "custom" && customOverrides) {
        body.portal_permissions = customOverrides;
      }
      // Iter 427.p41 — customer_role no longer sent; backend derives.
      await api.patch(`/portal/users/${uid}/permissions`, body);
      setEditingUid(null);
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not update permissions"));
    } finally {
      setBusy(false);
    }
  };

  const submitRemove = async (uid) => {
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/portal/users/${uid}`);
      setConfirmDeleteUid(null);
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not remove teammate"));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (!resetForUid) return;
    if (resetPwd.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/portal/users/${resetForUid}/set-password`, {
        new_password: resetPwd,
      });
      const u = users.find((x) => x.id === resetForUid);
      setTempCreds({ email: u?.email, password: resetPwd });
      setResetForUid(null);
      setResetPwd("");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  if (!canManage && !loading) {
    return (
      <div
        className="hv-card p-5"
        data-testid="portal-team-no-manage"
      >
        <ShieldCheck className="w-6 h-6 text-[#a0aabf] mb-2" />
        <h3 className="font-display text-lg font-bold">Team management restricted</h3>
        <p className="text-sm text-[#a0aabf] mt-1">
          Your account doesn't have the "Manage portal team" permission. Ask
          your account owner to grant you access.
        </p>
      </div>
    );
  }

  return (
    <div
      className={embedded ? "space-y-4" : "space-y-6"}
      data-testid="portal-team-manager"
    >
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">
              Customer Portal
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
              Team & access
            </h1>
            <p className="text-sm text-[#a0aabf] mt-1 max-w-xl">
              Invite people from your company to the portal. Each teammate
              gets the exact slice of access you choose — accounts only sees
              invoices, maintenance only sees jobs, and so on.
            </p>
          </div>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="hv-btn hv-btn-primary"
              data-testid="portal-team-add-btn"
            >
              <UserPlus className="w-4 h-4" /> Invite teammate
            </button>
          )}
        </div>
      )}

      {embedded && !adding && (
        <button
          onClick={() => setAdding(true)}
          className="hv-btn hv-btn-primary"
          data-testid="portal-team-add-btn"
        >
          <UserPlus className="w-4 h-4" /> Invite teammate
        </button>
      )}

      {err && (
        <div
          className="hv-card p-3 border border-rose-500/40 bg-rose-500/5 text-rose-200 text-sm"
          data-testid="portal-team-err"
        >
          {err}
        </div>
      )}

      {tempCreds && (
        <div
          className="hv-card p-4 border border-[#1fb6c1]/40 bg-[#1fb6c1]/5 space-y-3"
          data-testid="portal-team-temp-creds"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#1fb6c1] font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Access ready
            </div>
            <button
              onClick={() => setTempCreds(null)}
              className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-white"
            >
              Dismiss
            </button>
          </div>
          <CredRow label="Email" value={tempCreds.email} />
          {tempCreds.password ? (
            <CredRow label="Temp password" value={tempCreds.password} testId="portal-team-creds-pwd" />
          ) : (
            <p className="text-xs text-[#a0aabf]">
              No password set — share the app URL with your teammate and
              they'll set their own on first sign-in.
            </p>
          )}
        </div>
      )}

      {/* Add teammate panel (inline, no modal) */}
      {adding && (
        <div
          className="hv-card p-4 border border-[#1fb6c1]/30 bg-[#1fb6c1]/[0.03] space-y-3"
          data-testid="portal-team-add-panel"
        >
          <div className="flex items-center justify-between">
            <div className="font-display text-lg font-bold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#1fb6c1]" /> Invite teammate
            </div>
            <button
              onClick={resetAddForm}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="portal-team-add-cancel"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Email *">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setEmailLookup(null); }}
                onBlur={preflightEmail}
                className="hv-input"
                placeholder="staff@yourcompany.com"
                data-testid="portal-team-add-email"
              />
            </Field>
            <Field label="Name">
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="hv-input"
                placeholder="Jane Smith"
                data-testid="portal-team-add-name"
              />
            </Field>
          </div>
          {emailLookup?.exists && emailLookup?.already_on_this_customer && (
            <div
              className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200"
              data-testid="portal-team-add-already-member"
            >
              This email is already a portal user for your customer.
            </div>
          )}
          {emailLookup?.exists && emailLookup?.is_staff && (
            <div
              className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200"
              data-testid="portal-team-add-staff-email"
            >
              This email belongs to a staff account and cannot be added as a portal user.
            </div>
          )}
          {emailLookup?.exists
            && !emailLookup?.already_on_this_customer
            && !emailLookup?.is_staff && (
            <div
              className="text-xs px-3 py-2 rounded border border-[#1fb6c1]/40 bg-[#1fb6c1]/10 text-[#a0e8ee]"
              data-testid="portal-team-add-existing-identity"
            >
              This email already has a Hidroverse account
              {emailLookup.name ? ` (${emailLookup.name})` : ""}.
              They&rsquo;ll use their existing password to sign in and pick your
              customer at the login screen. The password field is hidden — a
              single identity has ONE password across every customer they belong to.
            </div>
          )}
          <PresetPicker
            value={presetInput}
            onChange={setPresetInput}
            testIdPrefix="portal-team-add-preset"
          />
          {presetInput === "custom" && (
            <CustomPermsEditor
              value={customInput}
              onChange={setCustomInput}
              testIdPrefix="portal-team-add-custom"
            />
          )}
          {!(emailLookup?.exists && emailLookup?.has_password) && (
            <Field label="Password (optional — leave blank to let them set their own)">
              <input
                type="text"
                value={pwdInput}
                onChange={(e) => setPwdInput(e.target.value)}
                className="hv-input font-mono"
                placeholder="Optional"
                data-testid="portal-team-add-pwd"
              />
            </Field>
          )}
          <div className="flex gap-2">
            <button
              onClick={submitAdd}
              disabled={busy || !emailInput.trim()}
              className="hv-btn hv-btn-primary flex-1"
              data-testid="portal-team-add-submit"
            >
              <UserPlus className="w-4 h-4" /> {busy ? "Inviting…" : "Send invite"}
            </button>
          </div>
        </div>
      )}

      {/* Existing team list */}
      <div className="space-y-2" data-testid="portal-team-list">
        {loading ? (
          <div className="text-sm text-[#647087]">Loading team…</div>
        ) : users.length === 0 ? (
          <div className="hv-card p-5 text-sm text-[#647087] italic">
            No teammates yet — invite your first one above.
          </div>
        ) : (
          users.map((u) => (
            <TeamRow
              key={u.id}
              user={u}
              isMe={me?.id === u.id}
              editing={editingUid === u.id}
              confirmDelete={confirmDeleteUid === u.id}
              resetting={resetForUid === u.id}
              resetPwd={resetPwd}
              busy={busy}
              onEdit={() => setEditingUid(u.id)}
              onCancelEdit={() => setEditingUid(null)}
              onSavePreset={(preset, custom) =>
                submitChangePreset(u.id, preset, custom)
              }
              onAskDelete={() => setConfirmDeleteUid(u.id)}
              onCancelDelete={() => setConfirmDeleteUid(null)}
              onConfirmDelete={() => submitRemove(u.id)}
              onAskReset={() => {
                setResetForUid(u.id);
                setResetPwd("");
              }}
              onCancelReset={() => {
                setResetForUid(null);
                setResetPwd("");
              }}
              onChangeResetPwd={setResetPwd}
              onSubmitReset={submitReset}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ---------------- Row ---------------- */
function TeamRow({
  user,
  isMe,
  editing,
  confirmDelete,
  resetting,
  resetPwd,
  busy,
  onEdit,
  onCancelEdit,
  onSavePreset,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onAskReset,
  onCancelReset,
  onChangeResetPwd,
  onSubmitReset,
}) {
  const preset = user.portal_preset || "owner";
  const eff = user.effective_perms || {};
  const grantedCount = Object.values(eff).filter(Boolean).length;
  return (
    <div
      className="hv-card p-4 space-y-3"
      data-testid={`portal-team-row-${user.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-2">
            {user.name || user.email}
            {isMe && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
                You
              </span>
            )}
          </div>
          <div className="text-xs text-[#647087] font-mono truncate">{user.email}</div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5"
              data-testid={`portal-team-preset-${user.id}`}
            >
              {preset === "custom" ? "Custom" : preset}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#647087]">
              {grantedCount} / 11 permissions
            </span>
          </div>
        </div>
        {!editing && !confirmDelete && !resetting && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onEdit}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`portal-team-edit-${user.id}`}
            >
              <Settings2 className="w-3 h-3" /> Permissions
            </button>
            <button
              onClick={onAskReset}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`portal-team-reset-${user.id}`}
            >
              <KeyRound className="w-3 h-3" /> Reset password
            </button>
            {!isMe && (
              <button
                onClick={onAskDelete}
                className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:!text-rose-200"
                data-testid={`portal-team-remove-${user.id}`}
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        )}
      </div>

      {/* Inline edit-preset slide-down */}
      {editing && (
        <EditPresetPanel
          uid={user.id}
          initialPreset={preset}
          initialCustom={preset === "custom" ? eff : null}
          busy={busy}
          onCancel={onCancelEdit}
          onSave={onSavePreset}
        />
      )}

      {/* Inline confirm-delete */}
      {confirmDelete && (
        <div
          className="border border-rose-500/40 bg-rose-500/5 rounded p-3 flex items-center justify-between gap-2"
          data-testid={`portal-team-confirm-delete-${user.id}`}
        >
          <div className="text-sm">
            Remove portal access for <strong>{user.email}</strong>?
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancelDelete}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`portal-team-confirm-delete-cancel-${user.id}`}
            >
              Cancel
            </button>
            <button
              onClick={onConfirmDelete}
              disabled={busy}
              className="hv-btn text-xs bg-rose-500/80 hover:bg-rose-500 text-white border border-rose-500/60"
              data-testid={`portal-team-confirm-delete-yes-${user.id}`}
            >
              Yes, remove
            </button>
          </div>
        </div>
      )}

      {/* Inline reset-password */}
      {resetting && (
        <div
          className="border border-white/10 rounded p-3 flex items-center gap-2 flex-wrap"
          data-testid={`portal-team-reset-panel-${user.id}`}
        >
          <input
            autoFocus
            type="text"
            value={resetPwd}
            onChange={(e) => onChangeResetPwd(e.target.value)}
            className="hv-input text-sm flex-1 min-w-[160px]"
            placeholder="New password"
            data-testid={`portal-team-reset-input-${user.id}`}
          />
          <button
            onClick={onSubmitReset}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid={`portal-team-reset-confirm-${user.id}`}
          >
            Save
          </button>
          <button
            onClick={onCancelReset}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid={`portal-team-reset-cancel-${user.id}`}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Edit-preset panel ---------------- */
function EditPresetPanel({ uid, initialPreset, initialCustom, busy, onCancel, onSave }) {
  const [preset, setPreset] = useState(initialPreset);
  const [custom, setCustom] = useState(initialCustom || {});
  return (
    <div
      className="border border-white/10 rounded p-3 space-y-3"
      data-testid={`portal-team-edit-panel-${uid}`}
    >
      <PresetPicker
        value={preset}
        onChange={setPreset}
        testIdPrefix={`portal-team-edit-preset-${uid}`}
      />
      {preset === "custom" && (
        <CustomPermsEditor
          value={custom}
          onChange={setCustom}
          testIdPrefix={`portal-team-edit-custom-${uid}`}
        />
      )}
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`portal-team-edit-cancel-${uid}`}
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(preset, preset === "custom" ? custom : null)}
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs"
          data-testid={`portal-team-edit-save-${uid}`}
        >
          Save
        </button>
      </div>
    </div>
  );
}

/* ---------------- Preset picker ---------------- */
function PresetPicker({ value, onChange, testIdPrefix }) {
  const LABELS = {
    owner: "Owner",
    admin: "Admin",
    accounts: "Accounts",
    maintenance: "Maintenance",
    custom: "Custom",
  };
  const DESCRIPTIONS = {
    owner: "Full access — can manage team",
    admin: "Full access except team management",
    accounts: "Quotes, invoices, documents only",
    maintenance: "Jobs, tickets, photos, ordering",
    custom: "Tick exactly what they can see",
  };
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
        Preset
      </div>
      <div className="grid sm:grid-cols-5 gap-2">
        {PRESET_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`hv-card p-3 text-left transition ${
              value === p
                ? "border-[#1fb6c1] bg-[#1fb6c1]/10"
                : "border-white/10 hover:border-white/20"
            }`}
            data-testid={`${testIdPrefix}-${p}`}
          >
            <div className="text-xs font-bold uppercase tracking-wider">
              {LABELS[p]}
            </div>
            <div className="text-[10px] text-[#a0aabf] mt-0.5 leading-tight">
              {DESCRIPTIONS[p]}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Custom perms editor ---------------- */
function CustomPermsEditor({ value, onChange, testIdPrefix }) {
  const toggle = (k) => {
    onChange({ ...value, [k]: !value[k] });
  };
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
        Pick exactly what they can access
      </div>
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.label}>
          <div className="text-xs uppercase tracking-wider text-[#647087] mb-1.5">
            {g.label}
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {g.keys.map((k) => (
              <label
                key={k}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/[0.02] p-1.5 rounded"
                data-testid={`${testIdPrefix}-${k}`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(value[k])}
                  onChange={() => toggle(k)}
                  className="rounded border-white/20"
                />
                <span>{PERM_LABELS[k] || k}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Helpers ---------------- */
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf] block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function CredRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#647087]">
          {label}
        </div>
        <div
          className="font-mono text-sm truncate"
          data-testid={testId}
        >
          {value}
        </div>
      </div>
      <button
        onClick={() => navigator.clipboard.writeText(value || "")}
        className="hv-btn hv-btn-ghost text-xs"
        aria-label={`Copy ${label}`}
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}
